import type { LanguageCode } from './languages'
import { hotelGalleryImages, seoByLanguage, siteBaseUrl, siteLogoImage } from './seo'

export type PageSeo = {
  /** Page-specific title; " | Modi Cafe" is appended automatically unless `rawTitle` is set. */
  title: string
  description: string
  canonical: string
  image?: string | null
  noindex?: boolean
  /** Use `title` as-is with no brand suffix appended (e.g. the homepage's own fully custom title). */
  rawTitle?: boolean
}

export const siteTitleSuffix = 'Modi Cafe'

const resolveTitle = (seo: PageSeo) => (seo.rawTitle ? seo.title : `${seo.title} | ${siteTitleSuffix}`)

const ogLocaleByLanguage: Record<LanguageCode, string> = {
  tr: 'tr_TR',
  en: 'en_US',
  ru: 'ru_RU',
}

const absoluteUrl = (value: string) => {
  if (/^https?:\/\//i.test(value)) return value
  return `${siteBaseUrl}${value.startsWith('/') ? value : `/${value}`}`
}

function setMetaTag(attr: 'name' | 'property', key: string, content: string) {
  let tag = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)

  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attr, key)
    document.head.appendChild(tag)
  }

  tag.content = content
}

function removeMetaTag(attr: 'name' | 'property', key: string) {
  document.querySelector(`meta[${attr}="${key}"]`)?.remove()
}

function setCanonicalTag(href: string) {
  let tag = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')

  if (!tag) {
    tag = document.createElement('link')
    tag.rel = 'canonical'
    document.head.appendChild(tag)
  }

  tag.href = href
}

const jsonLdScriptId = 'seo-json-ld'

function setJsonLd(seo: PageSeo, siteName: string, resolvedImage: string | null) {
  let script = document.getElementById(jsonLdScriptId) as HTMLScriptElement | null

  if (!script) {
    script = document.createElement('script')
    script.id = jsonLdScriptId
    script.type = 'application/ld+json'
    document.head.appendChild(script)
  }

  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: resolveTitle(seo),
        description: seo.description,
        url: seo.canonical,
        ...(resolvedImage ? { primaryImageOfPage: { '@type': 'ImageObject', url: resolvedImage } } : {}),
      },
      {
        '@type': 'Organization',
        name: siteName,
        url: siteBaseUrl,
        logo: siteLogoImage,
      },
      {
        '@type': 'CafeOrCoffeeShop',
        name: siteName,
        url: siteBaseUrl,
        image: resolvedImage
          ? [resolvedImage, ...hotelGalleryImages.filter((image) => image !== resolvedImage)]
          : hotelGalleryImages,
        logo: siteLogoImage,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Yalova Termal',
          addressCountry: 'TR',
        },
      },
    ],
  })
}

/**
 * Writes `document.title` plus meta description/canonical/OG/Twitter/robots
 * tags and a small auto-generated JSON-LD block for the current page — the
 * client-side equivalent of what the old WordPress site's Yoast SEO plugin
 * produced per page. No SSR/prerendering here, so this only becomes visible
 * once the route's JS has run (fine for crawlers that execute JS, not a
 * substitute for true SSR).
 */
export function applySeo(seo: PageSeo, language: LanguageCode) {
  const siteName = seoByLanguage[language].hotelName
  const resolvedTitle = resolveTitle(seo)
  const resolvedImage = seo.image ? absoluteUrl(seo.image) : null

  document.title = resolvedTitle
  setMetaTag('name', 'description', seo.description)
  setCanonicalTag(seo.canonical)

  setMetaTag('property', 'og:title', resolvedTitle)
  setMetaTag('property', 'og:description', seo.description)
  setMetaTag('property', 'og:url', seo.canonical)
  setMetaTag('property', 'og:type', 'website')
  setMetaTag('property', 'og:site_name', siteName)
  setMetaTag('property', 'og:locale', ogLocaleByLanguage[language])

  setMetaTag('name', 'twitter:title', resolvedTitle)
  setMetaTag('name', 'twitter:description', seo.description)

  if (resolvedImage) {
    setMetaTag('property', 'og:image', resolvedImage)
    setMetaTag('property', 'og:image:secure_url', resolvedImage)
    setMetaTag('property', 'og:image:alt', `${siteName} kahvalti ve cafe atmosferi`)
    setMetaTag('name', 'twitter:card', 'summary_large_image')
    setMetaTag('name', 'twitter:image', resolvedImage)
  } else {
    removeMetaTag('property', 'og:image')
    removeMetaTag('property', 'og:image:secure_url')
    removeMetaTag('property', 'og:image:alt')
    setMetaTag('name', 'twitter:card', 'summary')
    removeMetaTag('name', 'twitter:image')
  }

  setMetaTag('name', 'robots', seo.noindex ? 'noindex, nofollow' : 'index, follow')

  setJsonLd(seo, siteName, resolvedImage)
}
